import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, parseApiError, type Me, type MatchSummary } from '../lib/api';
import { displayClanName } from '../lib/displayName';
import { epochToLocalDateTimeInputValue } from './MatchRosterInitPanel';

// ── MatchDateAgreementControl (T4, division fixtures & weekly schedule;
// extracted onto the match card, 2026-07-18 — Lisa's info-architecture call:
// "Divisions is for RESULTS, Matches is to CREATE and VIEW matches (incl.
// scheduling)") ──────────────────────────────────────────────────────────────
// Renders ONLY for a leader of one of the two clans on `match` — everyone
// else (including a non-leader member, an admin previewing, or a viewer with
// no leader match at all) sees nothing here, same as before this feature
// existed. Gate reuses the exact discordId === leader1Id/leader2Id check
// MatchCardLive.tsx's AgreementBanner already established for the (separate)
// captain-agreement gate — no fresh "am I this clan's leader" test invented,
// and no founder/admin override (shared/matchDateAgreement.ts's guards have
// none either — proposing/answering a date is a captain courtesy, not a
// privileged action).
//
// Three states, checked in the SAME priority order the backend's own field
// semantics imply (dateAgreementStatus and scheduledAt can theoretically
// coexist if a leader re-proposes after an already-agreed date, since
// assertMayProposeDate never blocks on existing state — see
// shared/matchDateAgreement.ts):
//   1. AWAITING -> pending proposal (proposer waits / other leader responds)
//   2. else scheduledAt set -> agreed
//   3. else -> propose control
//
// `weekRange` is OPTIONAL — this control originally lived inline on a
// division's weekly fixtures table (DivisionsScoreboardPanel), where it had a
// current-week window to softly nudge toward. On the match card there IS no
// week window, so the caller passes no `weekRange` at all: the datetime-local
// draft simply defaults to now, and the "outside this week" nudge (which only
// makes sense with a week to be outside of) never renders.
export function MatchDateAgreementControl({
  compId,
  match,
  me,
  weekRange,
  onChanged,
}: {
  compId: string;
  match: MatchSummary;
  me?: Me;
  /** The current week's [start, end) — the SOFT default/preference; never enforced. Omit when there's no week context (e.g. the match card). */
  weekRange?: { start: number; end: number };
  onChanged: () => void;
}) {
  const matchId = match.sk;
  const isLeader1 = !!me?.discordId && me.discordId === match.leader1Id;
  const isLeader2 = !!me?.discordId && me.discordId === match.leader2Id;

  const [draft, setDraft] = useState(() => epochToLocalDateTimeInputValue(weekRange?.start ?? Date.now()));

  const proposeMutation = useMutation({
    mutationFn: (scheduledAt: number) => api.proposeMatchDate(matchId, compId, scheduledAt),
    onSuccess: onChanged,
  });
  const agreeMutation = useMutation({
    mutationFn: () => api.agreeMatchDate(matchId, compId),
    onSuccess: onChanged,
  });
  const declineMutation = useMutation({
    mutationFn: () => api.declineMatchDate(matchId, compId),
    onSuccess: onChanged,
  });

  // Not a leader of either clan on this match — no control, no change from
  // pre-T4 behaviour (this is the ENTIRE gate; everything below assumes one
  // of the two is true).
  if (!isLeader1 && !isLeader2) return null;

  const opponentLabel = displayClanName(isLeader1 ? match.clan2 : match.clan1);

  // ── 1. Pending proposal ──────────────────────────────────────────────────
  if (match.dateAgreementStatus === 'AWAITING') {
    const proposedLabel = typeof match.proposedScheduledAt === 'number' ? formatScheduledAt(match.proposedScheduledAt) : '—';
    const iAmProposer = !!me?.discordId && me.discordId === match.dateProposedBy;

    if (iAmProposer) {
      return (
        <div className="mt-1 rounded-md bg-white/[0.03] px-2 py-1 text-[11px] text-ink-secondary">
          Waiting for {opponentLabel} to agree to <span className="font-medium text-ink-primary">{proposedLabel}</span>
        </div>
      );
    }

    const respondError = agreeMutation.error ?? declineMutation.error;
    return (
      <div className="mt-1 flex flex-wrap items-center justify-between gap-2 rounded-md bg-accent-500/[0.06] px-2 py-1">
        <span className="min-w-0 text-[11px] text-ink-secondary">
          {opponentLabel} proposed <span className="font-medium text-ink-primary">{proposedLabel}</span>
        </span>
        <span className="flex shrink-0 gap-1.5">
          <button
            type="button"
            onClick={() => agreeMutation.mutate()}
            disabled={agreeMutation.isPending || declineMutation.isPending}
            className="rounded-md bg-accent-500/15 px-2 py-0.5 text-[11px] font-semibold text-accent-500 hover:bg-accent-500/25 disabled:opacity-50"
          >
            {agreeMutation.isPending ? 'Agreeing…' : 'Agree'}
          </button>
          <button
            type="button"
            onClick={() => declineMutation.mutate()}
            disabled={agreeMutation.isPending || declineMutation.isPending}
            className="rounded-md bg-white/[0.06] px-2 py-0.5 text-[11px] font-medium text-ink-secondary hover:bg-white/[0.1] disabled:opacity-50"
          >
            {declineMutation.isPending ? 'Declining…' : 'Decline'}
          </button>
        </span>
        {respondError != null && (
          <p className="w-full text-[10px] text-red-400">{parseApiError(respondError).message}</p>
        )}
      </div>
    );
  }

  // ── 2. Agreed ─────────────────────────────────────────────────────────────
  if (typeof match.scheduledAt === 'number') {
    return (
      <div className="mt-1 flex items-center justify-between gap-2 rounded-md bg-white/[0.03] px-2 py-1">
        <span className="min-w-0 truncate text-[11px] text-ink-secondary">
          Scheduled for <span className="font-medium text-ink-primary">{formatScheduledAt(match.scheduledAt)}</span>
        </span>
        <Link
          to={`/matchcard/${encodeURIComponent(matchId)}?compId=${encodeURIComponent(compId)}`}
          className="shrink-0 text-[11px] font-medium text-accent-500 hover:underline"
        >
          Open match card
        </Link>
      </div>
    );
  }

  // ── 3. No proposal, no agreed date — propose ────────────────────────────
  const chosenMs = draft ? new Date(draft).getTime() : NaN;
  const outsideWindow = weekRange != null && !Number.isNaN(chosenMs) && (chosenMs < weekRange.start || chosenMs >= weekRange.end);

  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5 rounded-md bg-white/[0.03] px-2 py-1">
      <input
        type="datetime-local"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        className="rounded border border-white/10 bg-white/[0.04] px-1.5 py-0.5 text-[11px] text-ink-primary"
      />
      <button
        type="button"
        onClick={() => { if (!Number.isNaN(chosenMs)) proposeMutation.mutate(chosenMs); }}
        disabled={proposeMutation.isPending || Number.isNaN(chosenMs)}
        className="rounded-md bg-accent-500/15 px-2 py-0.5 text-[11px] font-semibold text-accent-500 hover:bg-accent-500/25 disabled:opacity-50"
      >
        {proposeMutation.isPending ? 'Proposing…' : 'Propose date & time'}
      </button>
      {/* Soft preference only — NEVER blocks submission, just nudges. Only
          meaningful when the caller supplied a week window (a division
          fixture); the match card passes none, so this never renders there. */}
      {outsideWindow && (
        <span className="w-full text-[10px] text-amber-300">
          Outside this fixture's week — that's allowed (e.g. for a carried-over game).
        </span>
      )}
      {proposeMutation.isError && (
        <span className="w-full text-[10px] text-red-400">{parseApiError(proposeMutation.error).message}</span>
      )}
    </div>
  );
}

function formatScheduledAt(ms: number): string {
  return new Date(ms).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
